<template>
  <div class="file-group">
    <Suspense v-for="file in files" :key="file.row_index">
      <jr-file-input
        :fileType="fileType"
        :allowExts="allowExts"
        :nullable="true"
        v-model="file.file"
        @update:selected="file.selected = $event"
        @change="change($event)"
        @selectedFileId="selectedFileId($event)"
        :ref="setFileInputRef"
        :disabled="!isEdit"
        :size="size"
        :confirmDialog="immediateUpload"
      />
    </Suspense>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, reactive, watch, onBeforeUpdate, computed } from 'vue'
import JrFileInput from '@/components/jr-parts/JrFileInput.vue'
import { FileTypes } from '@/constants/enums'
import { APPLICANT_UPLOAD_FILE_EXTS, JR_UPLOAD_FILE_EXTS } from '@/constants/enums'

let rowIndex = 0

interface FileRow {
  file: number | null | undefined
  selected: boolean
  row_index: number
}

interface FileInfo {
  file: number | null
}

/**
 * 申請情報・添付ファイル選択コンポーネント
 */
export default defineComponent({
  name: 'GeneralAttachedFiles',
  emits: ['update:modelValue', 'updatedFile', 'deletedFileId'],
  components: {
    JrFileInput,
  },
  props: {
    modelValue: {
      type: Array as PropType<FileInfo[]>,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: true,
    },
    isJrExts: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
    },
    immediateUpload: {
      type: Boolean,
      required: false,
      default: false,
    },
    // メッセージと同時に送るタイプか
    isInMessage: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, context) {
    const files = reactive<FileRow[]>([])
    const allowExts = computed(() => {
      return props.isJrExts ? JR_UPLOAD_FILE_EXTS : APPLICANT_UPLOAD_FILE_EXTS
    })

    // ファイル選択ref
    let fileInputRefs = reactive<InstanceType<typeof JrFileInput>[]>([])
    const setFileInputRef = (el: InstanceType<typeof JrFileInput>) => {
      if (el) {
        fileInputRefs.push(el)
      }
    }

    // ファイルを追加した場合に、追加したファイル情報を親に通知
    const change = async (fileId: number | null) => {
      if (fileId == null) {
        return
      }

      // ファイルIDがあるものからfileのみ抜き出す
      const fileIds = files
        .filter((file) => file.file != null)
        .map((file) => {
          return { file: file.file }
        })
      context.emit('update:modelValue', fileIds)
      context.emit('updatedFile', fileId)
    }

    // ファイルを削除した場合、削除したファイルIDを親に通知
    const selectedFileId = async (fileId: number | null) => {
      if (fileId == null) {
        return
      }
      context.emit('deletedFileId', fileId)
    }

    onBeforeUpdate(() => {
      fileInputRefs.splice(0)
    })
    // 引数のファイルリストが変更されたらファイル情報を同期する
    watch(
      () => props.modelValue,
      () => {
        if (
          props.modelValue.length === files.length &&
          props.modelValue.every((file, index) => file.file === files[index].file)
        ) {
          // 内容が同じ場合、何もしない
          return
        } else {
          // 内容が異なる場合揃える
          // 削除されたものを削除する
          const deletes = files.filter((file) => {
            // 親に無く、自分にあるものを抽出
            return props.modelValue.findIndex((propFile) => propFile.file === file.file) === -1
          })
          deletes.forEach((deleteFile) => {
            // 削除する
            const index = files.findIndex((file) => file.row_index === deleteFile.row_index)
            files.splice(index, 1)
          })

          const adds = props.modelValue.filter((propFile) => {
            // 自分に無く、親にあるものを抽出
            return files.findIndex((file) => file.file === propFile.file) === -1
          })
          adds.forEach((file) => {
            files.push({
              file: file.file,
              selected: true,
              row_index: rowIndex++,
            })
          })
        }
      },
      { immediate: true }
    )

    watch(
      () => [files, props.isEdit],
      () => {
        if (props.isEdit && files.filter((file) => file.selected).length === files.length) {
          // 編集時は全て選択済みの場合行を追加する
          files.push({
            file: null,
            selected: false,
            row_index: rowIndex++,
          })
        } else if (files.length - files.filter((file) => file.selected).length >= 2) {
          // 未選択時が2行以上できた場合、削除する
          // 空行を取得
          const emptyIndex = files.findIndex((file) => !file.selected)
          // 削除
          files.splice(emptyIndex, 1)
        }
      },
      {
        immediate: true,
        deep: true,
      }
    )

    const allUpload = async (errorCallback?: () => void) => {
      for (const ref of fileInputRefs) {
        await ref.uploadFile(errorCallback, props.isInMessage)
      }
      // ファイルIDがあるものからfileのみ抜き出す
      const fileIds = files
        .filter((file) => file.file != null)
        .map((file) => {
          return { file: file.file }
        })
      // ファイルIDをemit
      context.emit('update:modelValue', fileIds)
    }

    return {
      files,
      fileType: FileTypes.PROXIMITY_OTHER,
      allowExts,
      setFileInputRef,
      allUpload,
      change,
      selectedFileId,
    }
  },
})
</script>

<style lang="scss" scoped>
.file-group {
  padding-top: -2.5px;
  padding-bottom: -2.5px;
  ::v-deep(.jr-file-input) {
    padding-top: 2.5px;
    padding-bottom: 2.5px;
    ::v-deep(.file-open-btn) {
      padding-top: inherit;
    }
  }
}
</style>
